// Types (duplicated here to avoid circular dependency)
type JobData = {
  jobId: number;
  areaID: number;
  areaName: string;
  projectNumber: string;
  subdivision: string;
  stage5Bonus: number;
  closingBonus: number;
  jobNumber: string;
  salesPrice: number;
  jobAddress: string;
  closeDate: string;
  stageCode: string;
  stage5Date: string;
  bonusStatusId: number;
  cMs: CMData[];
  notes: string;
  company?: string;
  companyID: number;
  regionID: number;
  regionName?: string;
  year?: number;
  month?: number;
  statusName?: string;
  createdOn?: string;
  createdBy?: string;
  pmReviewer?: string;
  pmReviewedOn?: string;
  opsReviewer?: string;
  opsReviewedOn?: string;
  updatedOn?: string;
  updatedBy?: string;
  spmReviewer?: string;
  spmReviewedOn?: string;
  dmReviewer?: string;
  dmReviewedOn?: string;
  payrollProcessor?: string;
  payrollProcessedOn?: string;
  projectedCloseDate?: string;
};

type CMData = {
  employeeNumber: string;
  cm: string;
  bonusPercent: number;
  bonusAmount: number;
  bonusType: string;
  paidDate?: string;
  initialCM?: string;
  initialBonusPercent?: number;
  initialBonusAmount?: number;
  initialBonusType?: string;
};

export const isApprovedJob = (bonusStatusId: number): boolean => {
  return [6, 9, 10].includes(bonusStatusId);
};

export const getMaxCMs = (bonusType: string): number => {
  return bonusType === "Stage5Bonus" ? 2 : 3;
};

export const formatDate = (date: string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return new Date(date).toLocaleDateString();
  } catch {
    return date || 'N/A';
  }
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

// Generate column headers for Approved jobs
export const getApprovedColumns = (bonusType: string): string[] => {
  const maxCMs = getMaxCMs(bonusType);
  const columns = [
    'Company', 'CompanyID', 'RegionID', 'RegionName', 'JobId', 'Area Name', 
    'Subdivision', 'Stage5 Bonus', 'Closing Bonus', 'Job Number', 'Sales Price', 
    'Job Address', 'Close Date', 'Stage Code', 'Stage5 Date', 'Projected Close Date'
  ];
  
  // Add CM columns for each CM (up to maxCMs)
  for (let i = 1; i <= maxCMs; i++) {
    columns.push(
      `Employee Number ${i}`, `CM ${i}`, `Bonus % ${i}`, `Bonus Amount${i}`, 
      `Bonus Type${i}`, `Paid Date${i}`
    );
  }
  
  // Add Initial CM columns for each CM (up to maxCMs)
  for (let i = 1; i <= maxCMs; i++) {
    columns.push(
      `Initial CM${i}`, `Initial Bonus %${i}`, `Initial Bonus Amount${i}`, `Initial Bonus Type${i}`
    );
  }
  
  columns.push('Notes', 'Ops Approved On');
  return columns;
};

// Generate column headers for Pending jobs
export const getPendingColumns = (bonusType: string): string[] => {
  if (bonusType === "Stage5Bonus") {
    return [
      'Stage5Date', 'Year', 'Month', 'JobId', 'Company', 'CompanyID', 'RegionID', 'RegionName', 'AreaID', 'AreaName', 'Job Address', 
      'Project and Job Number', 'Subdivision', 'Status Name', 
      'Created On', 'Created By', 'PM Reviewer', 'PM Reviewed On', 
      'Ops Reviewer', 'Ops Reviewed On', 'Updated On', 'Updated By', 
      'SPM Reviewer', 'SPM Reviewed On', 'DM Reviewer', 'DM Reviewed On'
    ];
  } else {
    return [
      'CloseDate', 'Year', 'Month', 'JobId', 'Company', 'CompanyID', 'RegionID', 'RegionName', 'AreaID', 'AreaName', 'Job Address', 
      'Project and Job Number', 'Subdivision', 'Status Name', 
      'Created On', 'Created By', 'PM Reviewer', 'PM Reviewed On', 
      'Ops Reviewer', 'Ops Reviewed On', 'Updated On', 'Updated By', 
      'SPM Reviewer', 'SPM Reviewed On', 'DM Reviewer', 'DM Reviewed On'
    ];
  }
};

// Get cell value for Approved jobs
export const getApprovedCellValue = (
  row: JobData, 
  column: string, 
  bonusType: string
): string | number => {
  const maxCMs = getMaxCMs(bonusType);
  const cMs = row.cMs || [];
  
  switch (column) {
    case 'Company': return row.company || 'N/A';
    case 'CompanyID': return row.companyID || 0;
    case 'RegionID': return row.regionID || 0;
    case 'RegionName': return row.regionName || 'N/A';
    case 'JobId': return row.jobId || 0;
    case 'Area Name': return row.areaName || 'N/A';
    case 'Subdivision': return row.subdivision || 'N/A';
    case 'Stage5 Bonus': return row.stage5Bonus || 0;
    case 'Closing Bonus': return row.closingBonus || 0;
    case 'Job Number': return row.jobNumber || 'N/A';
    case 'Sales Price': return row.salesPrice || 0;
    case 'Job Address': return row.jobAddress || 'N/A';
    case 'Close Date': return formatDate(row.closeDate);
    case 'Stage Code': return row.stageCode || 'N/A';
    case 'Stage5 Date': return formatDate(row.stage5Date);
    case 'Projected Close Date': return formatDate(row.projectedCloseDate);
    case 'Notes': return row.notes || 'N/A';
    case 'Ops Approved On': return formatDate(row.opsReviewedOn);
    default:
      // Handle CM columns - extract CM index from column name
      if (column.startsWith('Employee Number ')) {
        const index = parseInt(column.replace('Employee Number ', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.employeeNumber || '') : '';
      }
      if (column.startsWith('CM ')) {
        const index = parseInt(column.replace('CM ', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.cm || '') : '';
      }
      if (column.startsWith('Bonus % ')) {
        const index = parseInt(column.replace('Bonus % ', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.bonusPercent || 0) : 0;
      }
      if (column.startsWith('Bonus Amount')) {
        const index = parseInt(column.replace('Bonus Amount', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.bonusAmount || 0) : 0;
      }
      if (column.startsWith('Bonus Type') && !column.startsWith('Initial Bonus Type')) {
        const index = parseInt(column.replace('Bonus Type', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.bonusType || '') : '';
      }
      if (column.startsWith('Paid Date')) {
        const index = parseInt(column.replace('Paid Date', '')) - 1;
        const cm = cMs[index];
        return cm ? formatDate(cm.paidDate) : '';
      }
      if (column.startsWith('Initial CM')) {
        const index = parseInt(column.replace('Initial CM', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.initialCM || '') : '';
      }
      if (column.startsWith('Initial Bonus %')) {
        const index = parseInt(column.replace('Initial Bonus %', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.initialBonusPercent || 0) : 0;
      }
      if (column.startsWith('Initial Bonus Amount')) {
        const index = parseInt(column.replace('Initial Bonus Amount', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.initialBonusAmount || 0) : 0;
      }
      if (column.startsWith('Initial Bonus Type')) {
        const index = parseInt(column.replace('Initial Bonus Type', '')) - 1;
        const cm = cMs[index];
        return cm ? (cm.initialBonusType || '') : '';
      }
      return 'N/A';
  }
};

// Get cell value for Pending jobs
export const getPendingCellValue = (row: JobData, column: string, bonusType: string): string | number => {
  switch (column) {
    case 'Stage5Date': return formatDate(row.stage5Date);
    case 'CloseDate': return formatDate(row.closeDate);
    case 'Year': return row.year || 'N/A';
    case 'Month': return row.month || 'N/A';
    case 'JobId': return row.jobId || 0;
    case 'Company': return row.company || 'N/A';
    case 'CompanyID': return row.companyID || 0;
    case 'RegionID': return row.regionID || 0;
    case 'RegionName': return row.regionName || 'N/A';
    case 'AreaID': return row.areaID || 0;
    case 'AreaName': return row.areaName || 'N/A';
    case 'Job Address': return row.jobAddress || 'N/A';
    case 'Project and Job Number': return row.jobNumber || 'N/A';
    case 'Subdivision': return row.subdivision || 'N/A';
    case 'Status Name': return row.statusName || 'N/A';
    case 'Created On': return formatDate(row.createdOn);
    case 'Created By': return row.createdBy || 'N/A';
    case 'PM Reviewer': return row.pmReviewer || 'N/A';
    case 'PM Reviewed On': return formatDate(row.pmReviewedOn);
    case 'Ops Reviewer': return row.opsReviewer || 'N/A';
    case 'Ops Reviewed On': return formatDate(row.opsReviewedOn);
    case 'Updated On': return formatDate(row.updatedOn);
    case 'Updated By': return row.updatedBy || 'N/A';
    case 'SPM Reviewer': return row.spmReviewer || 'N/A';
    case 'SPM Reviewed On': return formatDate(row.spmReviewedOn);
    case 'DM Reviewer': return row.dmReviewer || 'N/A';
    case 'DM Reviewed On': return formatDate(row.dmReviewedOn);
    default: return 'N/A';
  }
};

